import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { SessionService } from 'src/app/auth/session/session.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CohortService {
  cohortsUrl: string = environment.cohorts;

  constructor(
    private httpClient: HttpClient,
    private toastr: ToastrService,
    private sessionService: SessionService,
  ) {}

  async createCohort(req: CohortRequest): Promise<void> {
    return await this.httpClient
      .post<void>(`${this.cohortsUrl}/cohorts`, req)
      .toPromise();
  }

  async getCohorts(): Promise<Cohort[]> {
    const resp = await this.httpClient
      .get<Cohort[]>(`${this.cohortsUrl}/cohorts`)
      .toPromise();

    this.setCohortStatus(resp);
    return resp;
  }

  async getCohort(name: string): Promise<Cohort> {
    const resp = await this.httpClient
      .get<Cohort>(`${this.cohortsUrl}/cohorts/${encodeURIComponent(name)}`)
      .toPromise();
    this.setCohortStatus([resp]);
    return resp;
  }

  async saveCohortStatus(name: string, enable: boolean): Promise<void> {
    const user = this.sessionService.getCurrentSession().name;
    try {
      await this.httpClient
        .patch<void>(`${this.cohortsUrl}/cohorts/${encodeURIComponent(name)}`, {
          patchType: 'Status',
          status: enable ? 'enabled' : 'disabled',
          requestCreator: user,
        })
        .toPromise();
      this.toastr.success(`Cohort ${enable ? 'enabled' : 'disabled'}`);
    } catch {
      this.toastr.error(`Error ${enable ? 'enabling' : 'disabling'} cohort`);
    }
  }

  async deleteCohort(name: string): Promise<void> {
    const user = this.sessionService.getCurrentSession().name;
    try {
      await this.httpClient
        .delete<void>(
          `${this.cohortsUrl}/cohorts/${encodeURIComponent(name)}`,
          {
            body: { requestCreator: user },
          },
        )
        .toPromise();
      this.toastr.success('Cohort Deleted');
    } catch {
      this.toastr.error('Error deleting cohort');
    }
  }

  async approveCohortChanges(name: string, approvalStatus: string) {
    const user = this.sessionService.getCurrentSession().name;
    await this.httpClient
      .patch<void>(`${this.cohortsUrl}/cohorts/${encodeURIComponent(name)}`, {
        patchType: 'Approval',
        approvalStatus: approvalStatus,
        requestApprover: user,
      })
      .toPromise();
  }

  async getCohortChanges(name: string): Promise<CohortChanges> {
    return await this.httpClient
      .get<CohortChanges>(
        `${this.cohortsUrl}/cohorts/${encodeURIComponent(name)}/changes`,
      )
      .toPromise();
  }

  async updateCohort(req: CohortRequest): Promise<void> {
    return await this.httpClient
      .put<void>(
        `${this.cohortsUrl}/cohorts/${encodeURIComponent(req.cohortName)}`,
        req,
      )
      .toPromise();
  }

  async getDisabledCohorts(): Promise<CohortsResponse> {
    const resp = await this.httpClient
      .get<CohortsResponse>(`${this.cohortsUrl}/cohorts?status=disabled`)
      .toPromise();
    this.setCohortStatus(resp.cohorts);
    return resp;
  }

  async getActiveCohorts(): Promise<Cohort[]> {
    const cohorts = await this.httpClient
      .get<Cohort[]>(`${this.cohortsUrl}/cohorts?status=enabled`)
      .toPromise();
    return this.sortCohorts(cohorts) || [];
  }

  private sortCohorts(cohorts: Cohort[]): Cohort[] {
    return cohorts.sort((a, b) =>
      a.name
        .replace(/\s/g, '')
        .localeCompare(b.name.replace(/\s/g, '').toLowerCase()),
    );
  }

  private setCohortStatus(cohorts: Cohort[]) {
    cohorts.forEach((cohort) => {
      if (cohort.awaitingCreationApproval) {
        cohort.status = CohortStatus.AwaitingApproval;
      } else if (cohort.disabledDate) {
        cohort.status = CohortStatus.Disabled;
      } else {
        cohort.status = CohortStatus.Enabled;
      }
    });
  }

  // private setStatus(cohort: Cohort) {
  //   let status: CohortStatus;
  //   if (cohort.awaitingCreationApproval) {
  //     status = CohortStatus.AwaitingApproval;
  //   } else if (cohort.disabledDate) {
  //     status = CohortStatus.Disabled;
  //   } else {
  //     status = CohortStatus.Enabled;
  //   }

  //   cohort.status = status;
  // }
}

export interface CohortRequest {
  cohortName: string;
  cohortDescription: string;
  entities: CohortEntity[];
  requestCreator: string;
  hasPendingApproval?: boolean;
  priority?: number;
}

export interface CohortsResponse {
  cohorts: Cohort[];
}

export class Cohort {
  name: string;
  description: string;
  entities: CohortEntity[];
  createdDate: string;
  disabledDate?: string;
  activeDate?: string;
  status?: CohortStatus;
  awaitingCreationApproval?: boolean;
  cohortCreator?: string;
  hasPendingApproval?: boolean;
  type?: string;
  priority?: number;
}

export interface CohortQuery {
  name: string;
  creator: string;
  status: CohortStatus[];
}

export interface CohortEntity {
  id: string;
  type: string;
}

export interface CohortChanges {
  name: string;
  cohortDescription: string;
  requestCreator: string;
  entitiesAdded: CohortEntity[];
  entitiesRemoved: CohortEntity[];
  timestamp: Date;
  statusChange: StatusChange;
  isDelete?: boolean;
}

export interface StatusChange {
  currentStatus: CohortStatus;
  proposedStatus: CohortStatus;
}

export enum ApprovalStatus {
  NoApprovalRequired = 'NoApprovalRequired',
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum CohortStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
  AwaitingApproval = 'Awaiting Approval',
}
